import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2 } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Terms() {
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/rounds/active`)
      .then(res => setRound(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#EAB308] animate-spin" />
      </div>
    );
  }

  const closeDate = round ? new Date(round.close_date) : null;
  const startDate = round ? new Date(round.start_date) : null;
  const fmt = (d) => d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'TBD';
  const fmtTime = (d) => d ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '20:00';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-testid="back-to-home">
              <ArrowLeft className="w-5 h-5" />
              <span>Home</span>
            </Link>
            <Link to="/" className="mx-auto">
              <img src="/logo.png" alt="Boss Win" className="h-20 w-auto" />
            </Link>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-gray-900 mb-8" data-testid="terms-title">
          Terms & Conditions
        </h1>

        <div className="prose prose-gray max-w-none space-y-8">
          {/* Section 1 */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">1. The Promoter</h2>
            <p className="text-gray-600 leading-relaxed">
              The promoter of this free prize draw is <strong>Botelho Brothers Construction Ltd</strong> (Company No. 15154170),
              trading as Boss Win ("we", "us", "our"), registered at 20 Elm Road, Thornton Heath, CR7 8RH, United Kingdom,
              and operated via the website <strong>bosswin.store</strong>.
            </p>
          </section>

          {/* Section 2 - Dynamic */}
          <section data-testid="draw-period-section">
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. Draw Period and Frequency</h2>
            <p className="text-gray-600 leading-relaxed">
              Draws take place on a <strong>weekly</strong> basis. The current draw round is{' '}
              <strong>Round #{round?.round_number || 'N/A'}</strong>, which opened on{' '}
              <strong>{fmt(startDate)}</strong> and closes on{' '}
              <strong>{fmt(closeDate)}</strong> at <strong>{fmtTime(closeDate)} GMT</strong>.
            </p>
            <p className="text-gray-600 leading-relaxed mt-2">
              After the closing date, no further entries (paid or free) will be accepted for that round. 
              A new round will automatically begin following the draw of the previous round.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">3. Eligibility</h2>
            <p className="text-gray-600 leading-relaxed">
              The draw is open to residents of the United Kingdom aged 18 years or older. Employees of Boss Win and their immediate family members are not eligible to enter.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">4. How to Enter</h2>
            <p className="text-gray-600 leading-relaxed mb-2">
              <strong>Paid Entry:</strong> Purchase one or more draw tickets via the website. Each paid ticket counts as one entry into the active draw round.
            </p>
            <p className="text-gray-600 leading-relaxed mb-2">
              <strong>Free Entry (No Purchase Necessary):</strong> Submit a free entry via the form on the homepage by providing your full name and email address. One free entry per person per round is permitted.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Both paid and free entries carry <strong>equal weight</strong> in the draw. No purchase is necessary to enter or win.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">5. The Draw</h2>
            <p className="text-gray-600 leading-relaxed">
              The winner is selected using a cryptographically verifiable random method. A unique random seed is generated, combined with the draw timestamp and total number of entries, then hashed using SHA-256. The resulting index determines the winner. The full draw proof (seed, timestamp, hash, and method) is recorded and published for transparency.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">6. Winner Notification</h2>
            <p className="text-gray-600 leading-relaxed">
              The winner will be notified by email within 48 hours of the draw. The winner must respond and claim their prize within <strong>14 days</strong> of notification. If the prize is not claimed within this period, we reserve the right to select an alternative winner or void the prize.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. Prize</h2>
            <p className="text-gray-600 leading-relaxed">
              The prize for each draw round will be specified on the website. Prizes are non-transferable and no cash alternative will be offered unless otherwise stated. The promoter reserves the right to substitute the prize with an alternative of equal or greater value.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. Data Protection</h2>
            <p className="text-gray-600 leading-relaxed">
              Personal data submitted will be used solely for the purposes of administering the draw and contacting winners. Winner names (first name and last initial) may be displayed on the website for transparency purposes.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">9. General</h2>
            <p className="text-gray-600 leading-relaxed">
              The promoter reserves the right to cancel, amend, or extend the draw at any time if deemed necessary. By entering, you agree to be bound by these terms and conditions. These terms are governed by the laws of England and Wales.
            </p>
          </section>

          {/* Dynamic update note */}
          <div className="mt-12 p-4 bg-gray-100 rounded-xl text-sm text-gray-500" data-testid="terms-update-note">
            <p>
              These terms are automatically updated to reflect the current draw round. 
              Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.
              {round && <span> Current round: <strong>#{round.round_number}</strong>, closing <strong>{fmt(closeDate)}</strong>.</span>}
            </p>
          </div>
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}
